/**
 * Visual Consistency Utilities
 * 
 * This file provides standardized styling utilities and patterns
 * to ensure visual consistency across the Hivetivity application.
 */

import { cn } from "@/lib/utils";
import { 
  badgeVariants, 
  interactionStates, 
  typography, 
  spacing, 
  borderRadius, 
  shadows 
} from "@/lib/design-tokens";

// Standardized component styling utilities
export const visualStyles = {
  
  // Card styling patterns
  card: {
    base: cn(
      borderRadius.components.card,
      shadows.components.card,
      "bg-card text-card-foreground border"
    ),
    hover: cn(
      "transition-all duration-200 ease-in-out",
      "hover:shadow-md hover:scale-[1.01]"
    ),
    interactive: cn(
      "cursor-pointer transition-all duration-200 ease-in-out",
      "hover:shadow-md hover:scale-[1.01]",
      "active:scale-[0.99]"
    ),
  },

  // Button styling patterns
  button: {
    base: cn(
      borderRadius.components.button,
      "font-medium text-sm",
      "transition-all duration-200 ease-in-out",
      "focus:outline-none focus:ring-2 focus:ring-offset-2"
    ),
    sizes: {
      sm: "h-8 px-3 text-xs",
      md: "h-10 px-4 text-sm", 
      lg: "h-12 px-6 text-base",
      icon: "h-10 w-10",
    },
    iconSpacing: {
      left: "mr-2",
      right: "ml-2",
    },
  },

  // Input styling patterns
  input: {
    base: cn(
      borderRadius.components.input,
      "h-10 px-3 py-2 text-sm",
      "border border-input bg-background",
      "transition-colors duration-200",
      "focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
      "disabled:cursor-not-allowed disabled:opacity-50"
    ),
    sizes: {
      sm: "h-8 px-2 text-xs",
      md: "h-10 px-3 text-sm",
      lg: "h-12 px-4 text-base",
    },
  },

  // Badge styling patterns
  badge: {
    base: cn(
      "inline-flex items-center font-medium",
      "transition-colors duration-200",
      borderRadius.components.badge
    ),
    sizes: {
      sm: "px-2 py-0.5 text-xs",
      md: "px-2.5 py-0.5 text-xs", 
      lg: "px-3 py-1 text-sm",
    },
    // Get standardized status badge styling
    getStatusStyle: (status: keyof typeof badgeVariants) => {
      const variant = badgeVariants[status] || badgeVariants.inactive;
      return cn(
        "px-2.5 py-0.5 text-xs font-medium",
        variant.classes,
        borderRadius.components.badge
      );
    },
  },

  // Table styling patterns
  table: {
    header: cn(
      "h-12 px-4 text-left align-middle font-medium text-muted-foreground",
      "[&:has([role=checkbox])]:pr-0"
    ),
    cell: cn(
      "px-4 py-4 align-middle",
      "[&:has([role=checkbox])]:pr-0"
    ),
    row: cn(
      "border-b transition-colors",
      "hover:bg-muted/50 data-[state=selected]:bg-muted"
    ),
    interactiveRow: cn(
      "border-b transition-colors cursor-pointer",
      "hover:bg-muted/50 data-[state=selected]:bg-muted",
      "active:bg-muted/75"
    ),
  },

  // Navigation styling patterns
  navigation: {
    item: cn(
      "flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium",
      "transition-all duration-200",
      "hover:bg-accent hover:text-accent-foreground"
    ),
    activeItem: cn(
      "bg-accent text-accent-foreground font-semibold",
      "shadow-sm"
    ),
    group: cn(
      "space-y-1"
    ),
    groupLabel: cn(
      "px-3 py-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider"
    ),
  },

  // Form styling patterns
  form: {
    field: cn("space-y-2"),
    fieldGrid: cn("grid gap-4"),
    label: cn(typography.components.label),
    description: cn(typography.components.caption, "mt-1"),
    error: cn("text-sm font-medium text-destructive"),
    section: cn("space-y-6"),
    actions: cn("flex justify-end gap-2 pt-6"),
    mobileActions: cn("flex flex-col-reverse gap-2 sm:flex-row sm:justify-end sm:pt-6"),
  },

  // Modal/Dialog styling patterns
  modal: {
    content: cn(
      borderRadius.components.modal,
      shadows.components.modal,
      "bg-background p-6 shadow-lg duration-200",
      "data-[state=open]:animate-in data-[state=closed]:animate-out",
      "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
    ),
    header: cn("space-y-1.5 text-center sm:text-left"),
    title: cn(typography.components.sectionTitle),
    description: cn(typography.components.body, "text-muted-foreground"),
    footer: cn("flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2"),
  },

  // Icon styling patterns
  icon: {
    getSizeClass: (size: keyof typeof spacing.icons) => spacing.icons[size],
    contexts: {
      button: spacing.icons.sm,
      navigation: spacing.icons.sm,
      table: spacing.icons.sm,
      title: spacing.icons.md,
      large: spacing.icons.lg,
    },
  },

  // Interactive states
  interactive: {
    hover: cn(
      "transition-all duration-200 ease-in-out",
      "hover:bg-accent/50 hover:text-accent-foreground"
    ),
    focus: cn(
      "focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
    ),
    active: cn(
      "active:scale-[0.98] active:bg-accent/75"
    ),
    disabled: cn(
      "disabled:pointer-events-none disabled:opacity-50"
    ),
  },

  // Typography utilities
  typography: {
    pageTitle: cn(typography.components.pageTitle),
    sectionTitle: cn(typography.components.sectionTitle),
    cardTitle: cn(typography.components.cardTitle),
    cardDescription: cn(typography.components.cardDescription),
    label: cn(typography.components.label),
    body: cn(typography.components.body),
    caption: cn(typography.components.caption),
  },
};

// Helper functions for consistent styling
export const styleHelpers = {
  
  /**
   * Get consistent card classes
   */
  getCardClasses: (interactive = false, className?: string) => {
    return cn(
      visualStyles.card.base,
      interactive && visualStyles.card.interactive,
      className
    );
  },

  /**
   * Get consistent button classes
   */
  getButtonClasses: (
    variant: 'default' | 'outline' | 'ghost' | 'destructive' = 'default',
    size: 'sm' | 'md' | 'lg' | 'icon' = 'md',
    className?: string
  ) => {
    return cn(
      visualStyles.button.base,
      visualStyles.button.sizes[size],
      className
    );
  },

  /**
   * Get consistent input classes
   */
  getInputClasses: (size: 'sm' | 'md' | 'lg' = 'md', className?: string) => {
    return cn(
      visualStyles.input.base,
      visualStyles.input.sizes[size],
      className
    );
  },

  /**
   * Get consistent badge classes for status
   */
  getStatusBadgeClasses: (status: keyof typeof badgeVariants, className?: string) => {
    return cn(
      visualStyles.badge.getStatusStyle(status),
      className
    );
  },

  /**
   * Get consistent table row classes
   */
  getTableRowClasses: (interactive = true, className?: string) => {
    return cn(
      interactive ? visualStyles.table.interactiveRow : visualStyles.table.row,
      className
    );
  },

  /**
   * Get consistent navigation item classes
   */
  getNavItemClasses: (active = false, className?: string) => {
    return cn(
      visualStyles.navigation.item,
      active && visualStyles.navigation.activeItem,
      className
    );
  },

  /**
   * Get consistent icon classes
   */
  getIconClasses: (
    context: 'button' | 'navigation' | 'table' | 'title' | 'large' = 'button',
    className?: string
  ) => {
    return cn(
      visualStyles.icon.contexts[context],
      className
    );
  },

  /**
   * Get consistent interactive element classes
   */
  getInteractiveClasses: (className?: string) => {
    return cn(
      visualStyles.interactive.hover,
      visualStyles.interactive.focus,
      visualStyles.interactive.active,
      className
    );
  },
};

// Validation utilities for style consistency
export const styleValidation = {
  
  /**
   * Check if color usage follows design tokens
   */
  validateColorUsage: (className: string): { valid: boolean; suggestions: string[] } => {
    const suggestions: string[] = [];
    
    // Check for hardcoded colors that should use design tokens
    const hardcodedPatterns = [
      { pattern: /bg-green-\d+/, suggestion: "Use badgeVariants.active for success states" },
      { pattern: /bg-red-\d+/, suggestion: "Use badgeVariants.withdrawn for error states" },
      { pattern: /bg-yellow-\d+/, suggestion: "Use badgeVariants.pending for warning states" },
      { pattern: /text-green-\d+/, suggestion: "Use design token text colors" },
      { pattern: /border-pink-\d+/, suggestion: "Use standard border colors from design tokens" },
    ];

    hardcodedPatterns.forEach(({ pattern, suggestion }) => {
      if (pattern.test(className)) {
        suggestions.push(suggestion);
      }
    });

    return {
      valid: suggestions.length === 0,
      suggestions,
    };
  },

  /**
   * Check if spacing follows standard scale
   */
  validateSpacing: (className: string): { valid: boolean; suggestions: string[] } => {
    const suggestions: string[] = [];
    
    // Check for non-standard spacing values
    const nonStandardSpacing = [
      /p-\[.*\]/, /m-\[.*\]/, /gap-\[.*\]/, /space-[xy]-\[.*\]/
    ];

    nonStandardSpacing.forEach(pattern => {
      if (pattern.test(className)) {
        suggestions.push("Use standard spacing scale instead of arbitrary values");
      }
    });

    return {
      valid: suggestions.length === 0,
      suggestions,
    };
  },

  /**
   * Check if typography follows standard patterns
   */
  validateTypography: (className: string): { valid: boolean; suggestions: string[] } => {
    const suggestions: string[] = [];
    
    // Check for inconsistent font sizes
    if (className.includes('text-') && !Object.values(typography.sizes).some(size => className.includes(size))) {
      suggestions.push("Use standard typography sizes from design tokens");
    }

    return {
      valid: suggestions.length === 0,
      suggestions,
    };
  },

  /**
   * Comprehensive style validation
   */
  validateAllStyles: (className: string) => {
    const colorValidation = styleValidation.validateColorUsage(className);
    const spacingValidation = styleValidation.validateSpacing(className);
    const typographyValidation = styleValidation.validateTypography(className);

    return {
      valid: colorValidation.valid && spacingValidation.valid && typographyValidation.valid,
      suggestions: [
        ...colorValidation.suggestions,
        ...spacingValidation.suggestions,
        ...typographyValidation.suggestions,
      ],
    };
  },
};

// Export commonly used combinations
export const commonStyles = {
  // Page layouts
  pageContainer: "container mx-auto px-4 py-6",
  pageHeader: "space-y-2 mb-6",
  pageContent: "space-y-6",
  
  // Card layouts
  cardGrid: "grid gap-6 md:grid-cols-2 lg:grid-cols-3",
  cardStack: "space-y-4",
  
  // Form layouts
  formContainer: "space-y-6 max-w-2xl",
  formSection: "space-y-4",
  formGrid: "grid gap-4 md:grid-cols-2",
  
  // Table layouts
  tableContainer: "rounded-md border",
  tableWrapper: "relative w-full overflow-auto",
  
  // Navigation layouts
  sidebarNav: "space-y-1 p-2",
  topNav: "flex items-center justify-between p-4 border-b",
  
  // Loading states
  loadingContainer: "flex items-center justify-center py-8",
  loadingSpinner: "animate-spin h-8 w-8 text-muted-foreground",
  
  // Empty states
  emptyContainer: "flex flex-col items-center justify-center py-12 text-center",
  emptyText: "text-muted-foreground mt-2",
};

export type VisualStyleKey = keyof typeof visualStyles;
export type StyleHelperKey = keyof typeof styleHelpers;